import { ChangeEvent, useEffect, useState } from 'react';
import { ChannelType } from '@trii/types/dist/Common/Channels';
import { IContactAddress } from '@trii/types/dist/Contacts';
import contactHelper from '../utils/contactHelper';
import editContactHelper from './utils/editContactHelper';

type InitialContactMethods =
  | {
      emails?: IContactAddress[];
      phones?: IContactAddress[];
      ims_whatsapp?: IContactAddress[];
      ims_rcs?: IContactAddress[];
      ims_facebook?: IContactAddress[];
      ims_webchat?: IContactAddress[];
      ims_instagram?: IContactAddress[];
      ims_mercadolibre?: IContactAddress[];
    }
  | null
  | undefined;

type DeleteAddressPayload = {
  contactId: string;
  contactAddressId: string;
  type: ChannelType;
};

type UseEditContactMethodsArgs = {
  entityId?: string | null;
  initial: InitialContactMethods;
  dispatch?: (action: any) => Promise<any>;
  deleteAddress: (payload: DeleteAddressPayload) => Promise<any> | any;
  onEntityUpdated?: (entity: unknown) => void;
};

// Maps a channel type to the entity field that holds its addresses, so a saved
// contact-method change can be synced back into the entity the same way the
// rest of the EditContactModal sections report their updates.
const CHANNEL_TYPE_ENTITY_KEY: Record<number, string> = {
  3: 'emails',
  5: 'phones',
  13: 'ims_whatsapp',
  14: 'ims_facebook',
  16: 'ims_instagram',
  20: 'ims_webchat',
  21: 'ims_rcs',
  41: 'ims_mercadolibre',
};

const useEditContactMethods = ({
  entityId,
  initial,
  dispatch,
  deleteAddress,
  onEntityUpdated,
}: UseEditContactMethodsArgs) => {
  // Notifies the parent entity of a persisted change to a contact-method list.
  // Only saved addresses are propagated so half-filled new entries never leak
  // into the entity.
  const notifyEntityUpdated = (channelType: ChannelType, list: any[]) => {
    if (!onEntityUpdated) return;
    const key = CHANNEL_TYPE_ENTITY_KEY[channelType as number];
    if (!key) return;
    const savedList = list.filter((item) => (item as any)?.isSaved !== false);
    onEntityUpdated({ [key]: savedList });
  };

  const [isDeletingAddress, setIsDeletingAddress] = useState<false | string>(false);

  const [initialSnapshot, setInitialSnapshot] = useState<InitialContactMethods>(() => initial);

  const [imsFacebooks, setImsFacebooks] = useState<any[]>(initial?.ims_facebook || []);
  const [imsMercadolibre, setImsMercadolibre] = useState<any[]>(initial?.ims_mercadolibre || []);
  const [imsWebchats, setImsWebchats] = useState<any[]>(initial?.ims_webchat || []);
  const [imsInstagrams, setImsInstagrams] = useState<any[]>(initial?.ims_instagram || []);
  const [imsWhatsapp, setImsWhatsApp] = useState<any[]>(initial?.ims_whatsapp || []);
  const [emails, setEmails] = useState<any[]>(initial?.emails || []);
  const [phoneNumbers, setPhoneNumbers] = useState<any[]>(initial?.phones || []);
  const [imsRCS, setImsRCS] = useState<any[]>(initial?.ims_rcs || []);

  const addPhone = () => {
    const newPhone = contactHelper.createNewAddress(ChannelType.PHONE);
    const newPhoneNumbers = [...phoneNumbers, newPhone];
    setPhoneNumbers(newPhoneNumbers as IContactAddress[]);
  };

  const addEmail = () => {
    const newEmail = contactHelper.createNewAddress(ChannelType.EMAIL);
    const newEmails = [...emails, newEmail];
    setEmails(newEmails as IContactAddress[]);
  };

  const addWhatsapp = () => {
    const newWhatsapp = contactHelper.createNewAddress(ChannelType.WHATSAPP);
    const newWhatsapps = [...imsWhatsapp, newWhatsapp];
    setImsWhatsApp(newWhatsapps as IContactAddress[]);
  };

  const addRCS = () => {
    const newRCS = contactHelper.createNewAddress(ChannelType.RCS);
    const newRCSs = [...imsRCS, newRCS];
    setImsRCS(newRCSs as IContactAddress[]);
  };

  const addWhatsappVerified = (newWhatsapp: IContactAddress) => {
    const updatedWhatsapps = [...imsWhatsapp, newWhatsapp];
    setImsWhatsApp(updatedWhatsapps);
    setInitialSnapshot((prev) => ({
      ...(prev ?? {}),
      ims_whatsapp: updatedWhatsapps,
    }));
    notifyEntityUpdated(ChannelType.WHATSAPP, updatedWhatsapps);
  };

  const replaceAddress = (addressId: string, newAddress: IContactAddress) => {
    if (!newAddress) {
      return;
    }

    if (newAddress.channelType === 3) {
      const newEmails = emails.map((email) => {
        if (email.id === addressId) {
          return newAddress;
        }
        return email;
      });

      setEmails(newEmails);
      setInitialSnapshot((prev) => ({
        ...(prev ?? {}),
        emails: newEmails,
      }));
      notifyEntityUpdated(newAddress.channelType, newEmails);
    }

    if (newAddress.channelType === 5) {
      const newPhoneNumbers = phoneNumbers.map((phoneNumber) => {
        if (phoneNumber.id === addressId) {
          return newAddress;
        }
        return phoneNumber;
      });

      setPhoneNumbers(newPhoneNumbers);
      setInitialSnapshot((prev) => ({
        ...(prev ?? {}),
        phones: newPhoneNumbers,
      }));
      notifyEntityUpdated(newAddress.channelType, newPhoneNumbers);
    }

    if (newAddress.channelType === 13) {
      const newImsWhatsapp = imsWhatsapp.map((item) => {
        if (item.id === addressId) {
          return newAddress;
        }
        return item;
      });

      setImsWhatsApp(newImsWhatsapp);
      setInitialSnapshot((prev) => ({
        ...(prev ?? {}),
        ims_whatsapp: newImsWhatsapp,
      }));
      notifyEntityUpdated(newAddress.channelType, newImsWhatsapp);
    }

    if (newAddress.channelType === 21) {
      const newImsRCS = imsRCS.map((item) => {
        if (item.id === addressId) {
          return newAddress;
        }
        return item;
      });

      setImsRCS(newImsRCS);
      setInitialSnapshot((prev) => ({
        ...(prev ?? {}),
        ims_rcs: newImsRCS,
      }));
      notifyEntityUpdated(newAddress.channelType, newImsRCS);
    }

    if (newAddress.channelType === 41) {
      const newImsMercadolibre = imsMercadolibre.map((item) => {
        if (item.id === addressId) {
          return newAddress;
        }
        return item;
      });

      setImsMercadolibre(newImsMercadolibre);
      setInitialSnapshot((prev) => ({
        ...(prev ?? {}),
        ims_mercadolibre: newImsMercadolibre,
      }));
      notifyEntityUpdated(newAddress.channelType, newImsMercadolibre);
    }
  };

  const handleAddressFieldChange = async (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldId: string,
    type: ChannelType,
  ) => {
    const newValue = e.target.value;

    if (type === 3) {
      const newEmails = editContactHelper.getAddressesWithUpdatedValues(newValue, fieldId, emails);
      setEmails(newEmails);
    } else if (type === 5) {
      const newPhoneNumbers = editContactHelper.getAddressesWithUpdatedValues(
        newValue,
        fieldId,
        phoneNumbers,
      );

      setPhoneNumbers(newPhoneNumbers);
    } else if (type === 13) {
      const newImsWhatsapp = editContactHelper.getAddressesWithUpdatedValues(
        newValue,
        fieldId,
        imsWhatsapp,
      );

      setImsWhatsApp(newImsWhatsapp);
    } else if (type === 21) {
      const newImsRCS = editContactHelper.getAddressesWithUpdatedValues(newValue, fieldId, imsRCS);

      setImsRCS(newImsRCS);
    } else if (type === 41) {
      const newImsMercadolibre = editContactHelper.getAddressesWithUpdatedValues(
        newValue,
        fieldId,
        imsMercadolibre,
      );
      setImsMercadolibre(newImsMercadolibre);
    }
  };

  const handleNoteFieldChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldId: string,
    addressType: ChannelType,
  ) => {
    if (addressType === 14) {
      const newImsFacebooks = imsFacebooks.map((imsFacebook) => {
        if (imsFacebook.id === fieldId) {
          return {
            ...imsFacebook,
            note: e.target.value,
          };
        }
        return imsFacebook;
      });
      setImsFacebooks(newImsFacebooks);
    }
    if (addressType === 20) {
      const newImsWebchats = imsWebchats.map((imsWebchat) => {
        if (imsWebchat.id === fieldId) {
          return {
            ...imsWebchat,
            note: e.target.value,
          };
        }
        return imsWebchat;
      });
      setImsWebchats(newImsWebchats);
    }
    if (addressType === 16) {
      const newImsInstagrams = imsInstagrams.map((imsInstagram) => {
        if (imsInstagram.id === fieldId) {
          return {
            ...imsInstagram,
            note: e.target.value,
          };
        }
        return imsInstagram;
      });
      setImsInstagrams(newImsInstagrams);
    }
    if (addressType === 3) {
      const newEmails = emails.map((email) => {
        if (email.id === fieldId) {
          return {
            ...email,
            note: e.target.value,
          };
        }
        return email;
      });
      setEmails(newEmails);
    }
    if (addressType === 5) {
      const newPhoneNumbers = phoneNumbers.map((phoneNumber) => {
        if (phoneNumber.id === fieldId) {
          return {
            ...phoneNumber,
            note: e.target.value,
          };
        }
        return phoneNumber;
      });
      setPhoneNumbers(newPhoneNumbers);
    }
    if (addressType === 13) {
      const newImsWhatsapp = imsWhatsapp.map((item) => {
        if (item.id === fieldId) {
          return {
            ...item,
            note: e.target.value,
          };
        }
        return item;
      });
      setImsWhatsApp(newImsWhatsapp);
    }
    if (addressType === 21) {
      const newImsRCS = imsRCS.map((item) => {
        if (item.id === fieldId) {
          return {
            ...item,
            note: e.target.value,
          };
        }
        return item;
      });

      setImsRCS(newImsRCS);
    }
    if (addressType === 41) {
      const newImsMercadolibre = imsMercadolibre.map((item) => {
        if (item.id === fieldId) {
          return {
            ...item,
            note: e.target.value,
          };
        }
        return item;
      });
      setImsMercadolibre(newImsMercadolibre);
    }
  };

  const handleDeleteAddress = async (fieldId: string, addressType: ChannelType) => {
    if (!entityId) {
      return;
    }

    if (
      addressType === 3 ||
      addressType === 5 ||
      addressType === 13 ||
      addressType === 14 ||
      addressType === 20 ||
      addressType === 16 ||
      addressType === 21 ||
      addressType === 41
    ) {
      setIsDeletingAddress(fieldId);
      const payload = {
        contactId: entityId,
        contactAddressId: fieldId,
        type: addressType,
      };
      if (dispatch) {
        await dispatch(deleteAddress(payload));
      } else {
        await deleteAddress(payload);
      }
      setIsDeletingAddress(false);
      switch (addressType) {
        case 3:
          const newEmails = emails.filter((email) => email.id !== fieldId);
          setEmails(newEmails);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            emails: newEmails,
          }));
          notifyEntityUpdated(addressType, newEmails);
          break;
        case 5:
          const newPhoneNumbers = phoneNumbers.filter((phoneNumber) => phoneNumber.id !== fieldId);
          setPhoneNumbers(newPhoneNumbers);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            phones: newPhoneNumbers,
          }));
          notifyEntityUpdated(addressType, newPhoneNumbers);
          break;
        case 13:
          const newImsWhatsapp = imsWhatsapp.filter((item) => item.id !== fieldId);
          setImsWhatsApp(newImsWhatsapp);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            ims_whatsapp: newImsWhatsapp,
          }));
          notifyEntityUpdated(addressType, newImsWhatsapp);
          break;
        case 21:
          const newImsRCS = imsRCS.filter((item) => item.id !== fieldId);
          setImsRCS(newImsRCS);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            ims_rcs: newImsRCS,
          }));
          notifyEntityUpdated(addressType, newImsRCS);
          break;
        case 41:
          const newImsMercadolibre = imsMercadolibre.filter((item) => item.id !== fieldId);
          setImsMercadolibre(newImsMercadolibre);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            ims_mercadolibre: newImsMercadolibre,
          }));
          notifyEntityUpdated(addressType, newImsMercadolibre);
          break;
        case 14:
          const newImsFacebooks = imsFacebooks.filter((item) => item.id !== fieldId);
          setImsFacebooks(newImsFacebooks);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            ims_facebook: newImsFacebooks,
          }));
          notifyEntityUpdated(addressType, newImsFacebooks);
          break;
        case 20:
          const newImsWebchats = imsWebchats.filter((item) => item.id !== fieldId);
          setImsWebchats(newImsWebchats);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            ims_webchat: newImsWebchats,
          }));
          notifyEntityUpdated(addressType, newImsWebchats);
          break;
        case 16:
          const newImsInstagrams = imsInstagrams.filter((item) => item.id !== fieldId);
          setImsInstagrams(newImsInstagrams);
          setInitialSnapshot((prev) => ({
            ...(prev ?? {}),
            ims_instagram: newImsInstagrams,
          }));
          notifyEntityUpdated(addressType, newImsInstagrams);
          break;
        default:
          break;
      }
    }
  };

  function resetToInitialContactInformation() {
    setImsFacebooks(initialSnapshot?.ims_facebook || []);
    setImsMercadolibre(initialSnapshot?.ims_mercadolibre || []);
    setImsWebchats(initialSnapshot?.ims_webchat || []);
    setImsInstagrams(initialSnapshot?.ims_instagram || []);
    setImsWhatsApp(initialSnapshot?.ims_whatsapp || []);
    setImsRCS(initialSnapshot?.ims_rcs || []);
    setEmails(initialSnapshot?.emails || []);
    setPhoneNumbers(initialSnapshot?.phones || []);
  }

  useEffect(() => {
    setInitialSnapshot(initial);
    if (initial?.emails && initial?.phones) {
      setEmails(initial.emails);
      setPhoneNumbers(initial.phones);
    }
    setImsWhatsApp(initial?.ims_whatsapp || []);
    setImsRCS(initial?.ims_rcs || []);
    setImsFacebooks(initial?.ims_facebook || []);
    setImsWebchats(initial?.ims_webchat || []);
    setImsInstagrams(initial?.ims_instagram || []);
    setImsMercadolibre(initial?.ims_mercadolibre || []);
  }, [initial]);

  return {
    field: {
      imsWhatsapp,
      imsRCS,
      imsMercadolibre,
      imsFacebooks,
      imsWebchats,
      imsInstagrams,
      emails,
      phoneNumbers,
    },
    action: {
      handleAddressFieldChange,
      handleNoteFieldChange,
      handleDeleteAddress,
      addPhone,
      addEmail,
      addWhatsapp,
      addRCS,
      replaceAddress,
      addWhatsappVerified,
      resetToInitialContactInformation,
    },
    state: {
      isDeletingAddress,
    },
  };
};

export default useEditContactMethods;
